'use client';

// ---------------------------------------------------------------------------
// useMusicSchedule — the clock that makes a schedule actually fire
// ---------------------------------------------------------------------------
//
// There is no DOM event for "it is now 07:00", so something has to wake up. The
// naive answer is setInterval polling every few seconds; this codebase has
// exactly one setInterval (core/lib/sync-bus.ts, for leader liveness) and it
// carries a comment justifying itself. This does the same, better:
//
//   1. While a track is PLAYING, the <audio> element's own `timeupdate` already
//      fires ~4x a second. The store's minute-resolution position is derived
//      from it, so re-evaluating when the minute changes costs nothing and needs
//      no timer at all.
//   2. While PAUSED, a single wake-up is armed for the next boundary — computed
//      from the schedule rows, not a poll. It uses the Web Animations API
//      (`el.animate(null, { duration })` + `await anim.finished`) rather than
//      setTimeout: it is declarative, cancellable via `anim.cancel()`, and ties
//      its lifetime to an element rather than a floating handle.
//   3. `visibilitychange` and `focus` re-evaluate immediately. This is what
//      corrects a late wake-up: browsers throttle background tabs heavily, so a
//      boundary crossed while hidden is caught the moment the tab comes back.
//
// MULTI-TAB. Two staff with the app open would otherwise mean two players
// starting the same playlist. `core/lib/sync-bus.ts` already elects a leader
// across tabs, so only the leader acts on a schedule; the others observe and
// display.
//
// AUTOPLAY. The browser rejects play() before a user gesture. A schedule firing
// beforehand raises a prompt rather than failing into silence — the store's
// `scheduleAwaitingGesture` carries that, and the panel shows it.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/core/lib/api-client';
import { getSyncBus } from '@/core/lib/sync-bus';
import { useTenantContext } from '@/core/providers/TenantProvider';

import { propertyNow } from '../lib/property-clock';
import {
  type ScheduleWindow,
  minutesUntilNextChange,
  resolveActiveSchedule,
} from '../lib/schedule';
import { useMusicStore } from '../stores/music.store';

interface SchedulesResponse {
  data: {
    schedules: (ScheduleWindow & { name: string; playlistId: string })[];
    playlists: { id: string; name: string; isActive: boolean; trackCount: number }[];
    timezone: string | null;
  };
}

/** How the schedule turns a playlist into a queue. */
export interface PlaylistLoader {
  (playlistId: string): Promise<string[]>;
}

export function useMusicSchedule(loadPlaylistTrackIds?: PlaylistLoader) {
  const scheduleEnabled = useMusicStore((s) => s.scheduleEnabled);
  const activeScheduleId = useMusicStore((s) => s.activeScheduleId);
  const positionSec = useMusicStore((s) => s.positionSec);
  const userHasInteracted = useMusicStore((s) => s.userHasInteracted);

  /**
   * Only ask when there is a hotel to ask about.
   *
   * This runner is mounted in MusicProvider, so it runs on EVERY page for EVERY
   * authenticated user. `/hotel/music/schedules` goes through `guardHotel` ->
   * `withStaffAuth`, which answers 403 WRONG_PORTAL_TYPE for a platform or
   * chain user — and `api-client` classifies an authenticated 403 as a FAULT
   * and writes it to error.log.
   *
   * So without this gate every super-admin page load wrote an error:
   *
   *   GET /api/v1/hotel/music/schedules 403 (Forbidden)
   *       at useMusicSchedule.ts:70
   *
   * That was observed in a real browser console, not theorised. It also undid
   * the access-log split, whose entire purpose is that error.log being empty
   * can be read literally. `retry: false` limited it to one request per mount;
   * it did not stop the request.
   */
  const { currentProperty } = useTenantContext();
  const hasProperty = Boolean(currentProperty?.id);

  const query = useQuery({
    queryKey: ['music', 'schedules'],
    queryFn: () => api.get<SchedulesResponse>('/hotel/music/schedules'),
    staleTime: 5 * 60 * 1000,
    enabled: hasProperty,
    retry: false,
  });

  /**
   * Memoised because the `?? []` fallback is a NEW array every render.
   *
   * Without this, `evaluate` is rebuilt each render and the arming effect below
   * re-runs — cancelling and re-arming the wake-up animation continuously, which
   * both wastes work and risks a boundary landing in the gap between cancel and
   * re-arm. The lint warning that caught it was right.
   */
  const schedules = useMemo(
    () => query.data?.data?.schedules ?? [],
    [query.data],
  );
  const timezone = query.data?.data?.timezone ?? null;

  /** Element the wake-up animation is attached to. */
  const clockElementRef = useRef<HTMLElement | null>(null);
  const animationRef = useRef<Animation | null>(null);
  /** Last minute-of-day we evaluated, so a 4 Hz tick does no repeat work. */
  const lastMinuteRef = useRef<number>(-1);

  /**
   * The window this tab has actually STARTED playing.
   *
   * Deliberately separate from the store's `activeScheduleId`, which is display
   * state every tab keeps in step. Using that one as the "already handled" guard
   * was a bug: it is set before the leader check, so a tab that was still a
   * candidate during election would mark the window handled and then
   * short-circuit every later evaluation — the schedule would silently never
   * fire. Tracking what we started, separately, means a non-leader simply
   * retries on the next tick.
   */
  const startedScheduleRef = useRef<string | null>(null);

  const evaluate = useCallback(async () => {
    if (!scheduleEnabled || schedules.length === 0) return;

    const { minuteOfDay, weekday } = propertyNow(timezone);
    const match = resolveActiveSchedule(schedules, minuteOfDay, weekday);
    const store = useMusicStore.getState();

    // Display state, kept current in every tab regardless of who acts.
    store.setActiveSchedule(match?.id ?? null);

    if (!match) {
      // Nothing scheduled now. The queue is left alone rather than stopped: a
      // window ending should not cut off music someone started by hand.
      startedScheduleRef.current = null;
      return;
    }

    // Already started this window in this tab.
    if (startedScheduleRef.current === match.id) return;

    // Only the leader acts, or every open tab starts the same playlist. A tab
    // that is not leader falls through and will try again on the next tick,
    // which matters during the election window right after load.
    if (!getSyncBus().isLeader()) return;

    if (!loadPlaylistTrackIds) return;
    const trackIds = await loadPlaylistTrackIds(match.playlistId);
    if (trackIds.length === 0) return;

    if (!store.userHasInteracted) {
      // Autoplay would be rejected. Ask instead of failing into silence, and do
      // NOT mark it started — once someone presses play it should begin.
      store.setScheduleAwaitingGesture(match.id);
      return;
    }

    startedScheduleRef.current = match.id;
    store.playNow(trackIds);
    store.play();
  }, [scheduleEnabled, schedules, timezone, loadPlaylistTrackIds]);

  // 1. Free tick while playing — the audio element is already firing timeupdate.
  useEffect(() => {
    if (!scheduleEnabled) return;
    const { minuteOfDay } = propertyNow(timezone);
    if (minuteOfDay === lastMinuteRef.current) return;
    lastMinuteRef.current = minuteOfDay;
    void evaluate();
  }, [positionSec, scheduleEnabled, timezone, evaluate]);

  // 2. One armed wake-up at the next boundary, re-armed on each fire.
  useEffect(() => {
    if (!scheduleEnabled || schedules.length === 0) return;
    if (typeof document === 'undefined' || typeof Animation === 'undefined') return;

    let cancelled = false;

    // A detached element is enough to own an animation; nothing is rendered.
    if (!clockElementRef.current) clockElementRef.current = document.createElement('div');
    const el = clockElementRef.current;

    const arm = () => {
      if (cancelled) return;
      const { minuteOfDay, weekday } = propertyNow(timezone);
      const minutes = minutesUntilNextChange(schedules, minuteOfDay, weekday);
      // Null means nothing ever changes — arm nothing rather than busy-wait.
      if (minutes === null) return;

      // A few seconds past the boundary, so a rounding difference between this
      // clock and the property clock cannot land us just before it and evaluate
      // to the same answer.
      const ms = minutes * 60_000 + 2_000;

      animationRef.current?.cancel();
      const anim = el.animate(null, { duration: ms });
      animationRef.current = anim;

      anim.finished
        .then(() => {
          if (cancelled) return;
          void evaluate();
          arm();
        })
        .catch(() => {
          // cancel() rejects the promise. Expected on re-arm and unmount.
        });
    };

    arm();

    return () => {
      cancelled = true;
      animationRef.current?.cancel();
      animationRef.current = null;
    };
  }, [scheduleEnabled, schedules, timezone, evaluate]);

  // 2b. The first user gesture unblocks autoplay — re-evaluate immediately.
  //
  // Without this the sequence stalled: a window opens while nobody has touched
  // the page, so `evaluate` cannot call play() and records
  // `scheduleAwaitingGesture` instead. The user then presses play by hand, which
  // sets `userHasInteracted` — but nothing re-ran the evaluation, so the
  // SCHEDULED playlist did not take over until the next minute boundary
  // happened to tick. The subscription on line above existed and was not acted
  // on, which is the kind of thing that looks intentional until you check.
  useEffect(() => {
    if (!scheduleEnabled || !userHasInteracted) return;
    // Force the minute guard to allow one re-evaluation.
    lastMinuteRef.current = -1;
    void evaluate();
  }, [userHasInteracted, scheduleEnabled, evaluate]);

  // 3. Correct a throttled or slept-through wake-up the moment we are visible.
  useEffect(() => {
    if (!scheduleEnabled) return;
    const onWake = () => {
      if (document.visibilityState === 'visible') void evaluate();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [scheduleEnabled, evaluate]);

  return {
    schedules,
    playlists: query.data?.data?.playlists ?? [],
    timezone,
    activeScheduleId,
    isLoading: hasProperty && query.isLoading,
    refetch: query.refetch,
  };
}

// Removed from this return: an `awaitingGesture` flag computed as
// `useMusicStore.getState().scheduleAwaitingGesture !== null`. It was wrong in a
// way that would have been hard to spot — `getState()` reads the store OUTSIDE
// the reactive subscription, so the value was a snapshot taken at render and
// would never have updated when a schedule actually started waiting. Nothing
// consumed it, so it was dead as well as broken. A consumer that needs it should
// subscribe: `useMusicStore((s) => s.scheduleAwaitingGesture)`.
